import React, { useState } from 'react';
import SectionWrapper from '../UI/SectionWrapper';
import { Quote, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation, Autoplay } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';

const testimonials = [
  {
    id: 1,
    text: "I have been living in the same house for twenty years. I love my house but  have always felt a sense of restlessness about living here. In the second half of last year, for the first time I felt a deep sense of contentment. The only thing that had changed is that Amelia did a land healing for me. I am very happy with the effect it has had and I'm feeling grateful. ",
    author: "MB",
    location: "Brisbane"
  },
  {
    id: 2,
    text: "Amelia did a remote healing on the 50 acres of land my family has lived on in California for more than 100 years. This is a place Amelia had never seen, so my family and I were quite surprised by the accuracy of the information she shared about the land. The effects that living over stagnant water can have on wellbeing felt very relevant and highlighted the impact our environment had been having. We enjoyed the process, and it felt great to be able to take this step to benefit both the property and our family. We believe it has the potential to have a profound effect.  I’d highly recommend Amelia’s services.!!",
    author: "MB",
    location: "California "
  },
  {
    id: 3,
    text: "I have to say, I was really skeptical at first. But when Amelia started talking about how everything is energy and that there’s more going on than we can see, I found it really interesting, it was a completely new concept for me. She identified areas of our property that we didn’t like going to, as well as spots we naturally gravitated toward due to the ley lines. I don’t think of myself as a very sensitive person, and I can’t quite put it into words, but it has definitely changed at our place for the better. I’m curious to see how it continues to evolve.",
    author: "BT",
    location: "Brisbane"
  },
  {
    id: 4,
    text: "I found the land healing a very new experience for me. I was excited to try it. I found Amelia so helpful in explaining the process and benefits. It was helpful to have a document to refer to. I found it very helpful and made a significant difference.",
    author: "CJ",
    location: "Brisbane"
  },
  {
    id: 5,
    text:  "I am so grateful to have Amelia do a Land Healing on my newish property a few months ago. It has helped me in some unexpected ways which just validates my experience even more. The main shift has been in my tendency to be out of the house doing all my hobbies and exercise etc, to becoming more drawn to spend time in my own space at home, particularly the outdoor area. \n\nThe process itself helped me tune into and connect to my new place in a way I wasn’t previously able to, and I have felt more connected and comfortable here since placing the crystals around my property. \n\nIt felt like I was meeting the land and energies here and I feel much more grounded and content. It was a wonderful experience and ritual to help me shift into my new home and create a deeper more balanced place from which to create my new life ahead of me. Thank you!!!",
    author: "JS",
    location: "Adelaide"
  }
];

const TestimonialCard = ({ item, onReadMore }) => {
  return (
    <div className="bg-white/10 p-8 rounded-2xl backdrop-blur-md border border-white/10 hover:bg-white/15 transition-all duration-300 flex flex-col h-full relative group overflow-hidden select-none">
      {/* Decorative Quote Icon */}
      <Quote className="absolute top-6 right-8 text-white/5 w-24 h-24 -rotate-12 group-hover:rotate-0 transition-transform duration-500" />
      
      <div className="relative z-10 flex-grow">
        <div className="text-sage-mist mb-4">
          <Quote className="w-8 h-8 fill-current opacity-80 text-emerald-400" />
        </div>
        
        {/* Text Area with Clamp */}
        <div className="mb-6 relative">
          <p className="text-lg leading-relaxed text-indigo-50/90 font-serif italic line-clamp-5">
            "{item.text}"
          </p>
        </div>
      </div>

      <div className="relative z-10 mt-auto pt-6 border-t border-white/10 flex items-end justify-between">
        <div>
          <div className="font-bold text-earthy-sand text-lg">{item.author}</div>
          <div className="text-xs text-sage-mist uppercase tracking-widest font-medium opacity-70">{item.location}</div>
        </div>
        
        <button 
          onClick={() => onReadMore(item)}
          className="text-white w-8 h-8 rounded-full border border-white/30 flex items-center justify-center hover:bg-white hover:text-emerald-900 transition-all duration-300 transform hover:scale-110"
          title="Read full story"
        >
          <span className="sr-only">Read more</span>
          <span className="text-xl leading-none mb-1">+</span>
        </button>
      </div>
    </div>
  );
};

const Testimonials = () => {
  const [selectedStory, setSelectedStory] = useState(null);

  return (
    <SectionWrapper id="testimonials" className="bg-emerald-950 text-indigo-50 py-24 relative overflow-hidden">
       {/* Background Elements */}
       <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20">
          <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-emerald-600/30 rounded-full blur-[100px]" />
          <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-emerald-800/20 rounded-full blur-[120px]" />
       </div>

      <div className="relative z-10 container mx-auto px-4">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-4xl md:text-5xl font-serif tracking-tight mb-4">
              Stories from the <span className="italic text-emerald-200 underline decoration-emerald-500/30 underline-offset-8">Land</span>
            </h2>
            <p className="text-indigo-50/60 text-lg">Real experiences from those who have healed their connection with their environment.</p>
          </div>
          
          <div className="flex gap-3">
            <button className="swiper-prev-btn w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-white hover:bg-white/10 transition-all duration-300">
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button className="swiper-next-btn w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white/70 hover:text-white hover:border-white hover:bg-white/10 transition-all duration-300">
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        <div className="testimonials-swiper-container">
          <Swiper
            modules={[Pagination, Navigation, Autoplay]}
            spaceBetween={30}
            slidesPerView={1}
            navigation={{
              prevEl: '.swiper-prev-btn',
              nextEl: '.swiper-next-btn',
            }}
            pagination={{ 
              clickable: true,
              dynamicBullets: true,
              el: '.swiper-custom-pagination'
            }}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
              pauseOnMouseEnter: true
            }}
            breakpoints={{
              640: {
                slidesPerView: 2,
              },
              1024: {
                slidesPerView: 3,
              },
            }}
            className="pb-16"
          >
            {testimonials.map((item) => (
              <SwiperSlide key={item.id} className="h-auto">
                <TestimonialCard item={item} onReadMore={setSelectedStory} />
              </SwiperSlide>
            ))}
          </Swiper>
          
          <div className="swiper-custom-pagination flex justify-center mt-4"></div>
        </div>
      </div>

      {/* Modal / Overlay */}
      {selectedStory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-md transition-opacity duration-300" 
            onClick={() => setSelectedStory(null)}
          />
          
          <div className="relative bg-emerald-950 text-indigo-50 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl border border-white/10 shadow-2xl p-8 md:p-12 animate-in fade-in zoom-in-95 duration-300 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-emerald-500/20 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-emerald-500/40">
            <button 
              onClick={() => setSelectedStory(null)}
              className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors p-2 rounded-full hover:bg-white/10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="flex flex-col items-center text-center">
              <Quote className="w-12 h-12 text-emerald-400 mb-8 opacity-80 fill-current" />
              
              <p className="text-xl md:text-2xl leading-relaxed font-serif italic text-indigo-50/90 mb-10 whitespace-pre-line">
                "{selectedStory.text}"
              </p>

              <div className="w-16 h-1 bg-emerald-500/30 rounded-full mb-6" />

              <div>
                <div className="font-bold text-xl text-earthy-sand">{selectedStory.author}</div>
                <div className="text-sm text-emerald-300/70 uppercase tracking-widest font-medium mt-1">{selectedStory.location}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .testimonials-swiper-container .swiper-pagination-bullet {
          background: rgba(255, 255, 255, 0.3);
          opacity: 1;
          width: 10px;
          height: 10px;
          transition: all 0.3s ease;
        }
        .testimonials-swiper-container .swiper-pagination-bullet-active {
          background: #10b981;
          width: 24px;
          border-radius: 5px;
        }
      `}} />
    </SectionWrapper>
  );
};

export default Testimonials;

